<script setup lang="ts">
import axios from 'axios'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'



const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ThingsBoard n'autorise l'écriture d'entité qu'aux administrateurs de tenant : sans ce test,
// les utilisateurs client verraient un bouton « Renommer » qui échoue systématiquement en 403.
async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface MonthlyConsumption {
    month: number
    label: string
    value: number | null
    daysOfUsage: number | null
}

declare interface DailyConsumption {
    day: number
    value: number | null
    hasUsage: boolean
    prevCumul: number | null
}

declare interface HourlyConsumption {
    hour: number
    value: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

/** Invisible filter — fetch both WMETER and WMCELL device types */
const DEVICE_TYPES = ['WMETER', 'Cell-Watermeter']

const allDevices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)
const deviceSearch = ref('')
const isLoadingChart = ref(false)
const monthlyConsumption = ref<MonthlyConsumption[]>([])
const latestCumul = ref<number | null>(null)
const latestCurrentFlow = ref<number | null>(null)
const latestBatteryVoltage = ref<number | null>(null)
const latestRssi = ref<number | null>(null)
const latestSnr = ref<number | null>(null)
const latestTs = ref<number | null>(null)
const isLoadingTelemetry = ref(false)

const currentYear = new Date().getFullYear()
const selectedYear = ref(currentYear)
const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i)
const monthNames = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
const fullMonthNames = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

// ── Calendar state ───────────────────────────────────────────────────────────

const calendarMonth = ref<number | null>(null)
const calendarYear = ref<number | null>(null)
const dailyData = ref<DailyConsumption[]>([])
const isLoadingCalendar = ref(false)

// Offset for first day of month (0 = Mon … 6 = Sun, French convention)
const calendarFirstDow = computed(() => {
    if (!calendarYear.value || !calendarMonth.value) return 0
    const d = new Date(calendarYear.value, calendarMonth.value - 1, 1).getDay()
    return d === 0 ? 6 : d - 1
})

// ── Hourly chart state ────────────────────────────────────────────────────────

const selectedDay = ref<number | null>(null)
const hourlyData = ref<HourlyConsumption[]>([])
const isLoadingHourly = ref(false)

const calendarCardRef = ref<HTMLElement | null>(null)
const hourlyCardRef = ref<HTMLElement | null>(null)

const hourlyChartOption = computed<ECOption>(() => {
    if (hourlyData.value.length === 0) return {}
    const labels = hourlyData.value.map(h => `${String(h.hour).padStart(2, '0')}h`)
    const values = hourlyData.value.map(h => h.value)
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            confine: false,
            enterable: false,
            axisPointer: { type: 'shadow' },
            position(point: number[]) {
                return [point[0] + 12, point[1] - 36]
            },
            formatter(params: unknown) {
                const items = params as Array<{ axisValueLabel: string; value: number | null; marker: string }>
                if (!items.length) return ''
                const p = items[0]
                return `${p.marker}<b>${p.axisValueLabel}</b>: ${p.value !== null ? p.value.toFixed(3) + ' m³' : '—'}`
            },
        },
        grid: { top: 24, left: '2%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: labels, axisTick: { alignWithLabel: true } },
        yAxis: { type: 'value', name: 'm³' },
        series: [{
            type: 'bar',
            data: values,
            itemStyle: { borderRadius: [3, 3, 0, 0] },
            label: {
                show: true,
                position: 'top',
                formatter: (p: { value: number | null }) =>
                    p.value !== null && p.value > 0 ? p.value.toFixed(2) : '',
                fontSize: 9,
            },
        }],
    }
})

// ── Duration helper ───────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const timeSinceLastTelemetry = computed(() =>
    latestTs.value ? formatDuration(Date.now() - latestTs.value) : null
)

// ── Filtered list ─────────────────────────────────────────────────────────────

const filteredDevices = computed<TbDevice[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return allDevices.value
    return allDevices.value.filter(d =>
        (d.label || d.name).toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q),
    )
})

// ── Monthly consumption chart ────────────────────────────────────────────────

const monthlyChartOption = computed<ECOption>(() => {
    if (monthlyConsumption.value.length === 0) return {}
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            confine: true,
            axisPointer: { type: 'shadow' },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: number | null; marker: string; axisValueLabel: string }>
                if (!items.length) return ''
                let html = `<b>${items[0].axisValueLabel}</b><br/>`
                for (const p of items) {
                    if (p.value == null) continue
                    const isDay = p.seriesName === "Jours d'usage"
                    const valStr = isDay ? String(p.value) : p.value.toFixed(2)
                    const unit = isDay ? ' j' : ' m³'
                    html += `${p.marker}${p.seriesName}: <b>${valStr}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: { top: 4 },
        grid: { top: 52, left: '2%', right: '5%', bottom: '3%', containLabel: true },
        xAxis: {
            type: 'category',
            data: monthlyConsumption.value.map(m => m.label),
            axisTick: { alignWithLabel: true },
        },
        yAxis: [
            { type: 'value', name: 'm³' },
            { type: 'value', name: 'jours', min: 0, splitLine: { show: false } },
        ],
        series: [
            {
                name: 'Consommation',
                type: 'bar',
                yAxisIndex: 0,
                data: monthlyConsumption.value.map(m => m.value),
                itemStyle: { borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (p: { value: number | null }) =>
                        p.value !== null && p.value > 0 ? p.value.toFixed(1) : '',
                    fontSize: 10,
                },
            },
            {
                name: "Jours d'usage",
                type: 'bar',
                yAxisIndex: 1,
                data: monthlyConsumption.value.map(m => m.daysOfUsage),
                itemStyle: { borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (p: { value: number | null }) =>
                        p.value !== null && p.value > 0 ? String(p.value) : '',
                    fontSize: 10,
                },
            },
        ],
    }
})

// ── Fetch WMETER devices (paginated, filtered server-side) ────────────────────

async function loadLatestTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    latestCumul.value = null
    latestCurrentFlow.value = null
    latestBatteryVoltage.value = null
    latestRssi.value = null
    latestSnr.value = null
    latestTs.value = null
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Cumul,data_CurrentFlow,data_BatV,rssi,snr`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        latestCumul.value = resp.data?.data_Cumul?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_Cumul[0].value) : null
        latestCurrentFlow.value = resp.data?.data_CurrentFlow?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_CurrentFlow[0].value) : null
        latestBatteryVoltage.value = resp.data?.data_BatV?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_BatV[0].value) : null
        latestRssi.value = resp.data?.rssi?.[0]?.value !== undefined
            ? parseFloat(resp.data.rssi[0].value) : null
        latestSnr.value = resp.data?.snr?.[0]?.value !== undefined
            ? parseFloat(resp.data.snr[0].value) : null
        latestTs.value = resp.data?.data_Cumul?.[0]?.ts ?? null
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Fetch WMETER + WMCELL devices (parallel, type-filtered) ─────────────────────

async function fetchByType(token: string, type: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(type)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result
}

async function fetchWmeterDevices(token: string): Promise<TbDevice[]> {
    const results = await Promise.all(DEVICE_TYPES.map(t => fetchByType(token, t)))
    return results
        .flat()
        .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

// ── Days of usage ────────────────────────────────────────────────────────────

async function fetchDaysOfUsage(
    token: string,
    deviceId: string,
    startTs: number,
    endTs: number,
    seedVal: number | null,
): Promise<number> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    if (readings.length === 0) return 0
    const usageDays = new Set<string>()
    let prevVal = seedVal
    for (const r of readings) {
        const v = parseFloat(r.value)
        if (prevVal !== null && v > prevVal) {
            usageDays.add(new Date(r.ts).toISOString().slice(0, 10))
        }
        prevVal = v
    }
    return usageDays.size
}

// ── Monthly telemetry fetch ──────────────────────────────────────────────────

async function fetchMonthlyConsumption(token: string, deviceId: string, year: number): Promise<MonthlyConsumption[]> {
    const now = new Date()
    const isCurrentYear = year === now.getFullYear()
    // For current year: fetch up to current month; for past years: all 12 months
    const lastKnownMonth = isCurrentYear ? now.getMonth() + 1 : 12

    // Fetch cumulative reading at the start of each period:
    // index 0 = Dec of previous year (baseline), 1..lastKnownMonth = start of each month
    const cumulReadings: (number | null)[] = []
    for (let m = 0; m <= lastKnownMonth; m++) {
        let startTs: Date
        let endTs: Date
        if (m === 0) {
            startTs = new Date(year - 1, 11, 1, 12, 0, 0, 0)
            endTs = new Date(year, 0, 1)
        } else {
            startTs = new Date(year, m - 1, 1, 12, 0, 0, 0)
            endTs = new Date(year, m, 1)
        }
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Cumul&startTs=${startTs.getTime()}&endTs=${endTs.getTime()}&limit=1`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const raw = resp.data?.data_Cumul?.[0]?.value
        cumulReadings.push(raw !== undefined ? parseFloat(raw) : null)
    }

    // For the current month in current year, use the latest reading
    let latestCumulValue: number | null = null
    if (isCurrentYear) {
        const latestUrl =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries?keys=data_Cumul`
        const latestResp = await axios.get(latestUrl, { headers: tbHeaders(token) })
        const raw = latestResp.data?.data_Cumul?.[0]?.value
        latestCumulValue = raw !== undefined ? parseFloat(raw) : null
    }


    // Parallel: count days of usage per known month
    const daysOfUsageArr = await Promise.all(
        Array.from({ length: lastKnownMonth }, (_, i) => {
            const m = i + 1
            return fetchDaysOfUsage(
                token,
                deviceId,
                new Date(year, m - 1, 1).getTime(),
                new Date(year, m, 1).getTime(),
                cumulReadings[m - 1],
            )
        })
    )

    const result: MonthlyConsumption[] = []
    for (let m = 1; m <= 12; m++) {
        if (m > lastKnownMonth) {
            result.push({ month: m, label: monthNames[m], value: null, daysOfUsage: null })
            continue
        }
        const prevCumul = cumulReadings[m - 1]
        const currCumul = m < lastKnownMonth
            ? cumulReadings[m]
            : (isCurrentYear ? latestCumulValue : cumulReadings[m])
        if (prevCumul === null || currCumul === null) {
            result.push({ month: m, label: monthNames[m], value: null, daysOfUsage: daysOfUsageArr[m - 1] })
        } else {
            result.push({
                month: m,
                label: monthNames[m],
                value: Math.max(0, parseFloat((currCumul - prevCumul).toFixed(2))),
                daysOfUsage: daysOfUsageArr[m - 1],
            })
        }
    }
    return result
}

// ── Daily consumption (calendar drill-down) ──────────────────────────────────

async function fetchDailyConsumption(token: string, deviceId: string, year: number, month: number): Promise<DailyConsumption[]> {
    const startTs = new Date(year, month - 1, 1).getTime()
    const endTs = new Date(year, month, 1).getTime()
    // Seed: last reading before the month
    const seedUrl =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&endTs=${startTs}&limit=1`
    const seedResp = await axios.get(seedUrl, { headers: tbHeaders(token) })
    const seedRaw = seedResp.data?.data_Cumul?.[0]?.value
    let prevVal: number | null = seedRaw !== undefined ? parseFloat(seedRaw) : null
    // All readings for the month ASC
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    // Last cumul value per calendar day
    const dayMap = new Map<number, number>()
    for (const r of readings) {
        dayMap.set(new Date(r.ts).getDate(), parseFloat(r.value))
    }
    const daysInMonth = new Date(year, month, 0).getDate()
    const result: DailyConsumption[] = []
    for (let d = 1; d <= daysInMonth; d++) {
        const dayVal = dayMap.get(d) ?? null
        if (dayVal !== null && prevVal !== null) {
            const consumption = Math.max(0, parseFloat((dayVal - prevVal).toFixed(3)))
            result.push({ day: d, value: consumption, hasUsage: consumption > 0, prevCumul: prevVal })
        } else {
            result.push({ day: d, value: null, hasUsage: false, prevCumul: prevVal })
        }
        if (dayVal !== null) prevVal = dayVal
    }
    return result
}

async function fetchHourlyConsumption(
    token: string,
    deviceId: string,
    year: number,
    month: number,
    day: number,
    seed: number | null,
): Promise<HourlyConsumption[]> {
    const dayStart = new Date(year, month - 1, day).getTime()
    const dayEnd = new Date(year, month - 1, day + 1).getTime()
    let prevVal: number | null = seed
    // All readings for the day ASC
    const resp = await axios.get(
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${dayStart}&endTs=${dayEnd}&limit=10000&orderBy=ASC`,
        { headers: tbHeaders(token) },
    )
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    // Last cumul value per hour
    const hourMap = new Map<number, number>()
    for (const r of readings) {
        hourMap.set(new Date(r.ts).getHours(), parseFloat(r.value))
    }
    const result: HourlyConsumption[] = []
    for (let h = 0; h < 24; h++) {
        const hVal = hourMap.get(h) ?? null
        if (hVal !== null && prevVal !== null) {
            result.push({ hour: h, value: Math.max(0, parseFloat((hVal - prevVal).toFixed(3))) })
        } else {
            result.push({ hour: h, value: null })
        }
        if (hVal !== null) prevVal = hVal
    }
    return result
}

async function openDayChart(day: number) {
    if (!selectedDevice.value || !calendarYear.value || !calendarMonth.value) return
    selectedDay.value = day
    isLoadingHourly.value = true
    hourlyData.value = []
    await nextTick()
    ;(hourlyCardRef.value as any)?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    try {
        const token = await getTbToken()
        if (!token) return
        const seed = dailyData.value.find(d => d.day === day)?.prevCumul ?? null
        hourlyData.value = await fetchHourlyConsumption(
            token, selectedDevice.value.id.id,
            calendarYear.value, calendarMonth.value, day, seed,
        )
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingHourly.value = false
    }
}

async function openMonthCalendar(month: number) {
    if (!selectedDevice.value) return
    calendarMonth.value = month
    calendarYear.value = selectedYear.value
    selectedDay.value = null
    hourlyData.value = []
    isLoadingCalendar.value = true
    dailyData.value = []
    await nextTick()
    ;(calendarCardRef.value as any)?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    try {
        const token = await getTbToken()
        if (!token) return
        dailyData.value = await fetchDailyConsumption(token, selectedDevice.value.id.id, selectedYear.value, month)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingCalendar.value = false
    }
}

function onMonthClick(params: { dataIndex: number }) {
    openMonthCalendar(params.dataIndex + 1)
}

async function loadMonthlyChart(deviceId: string) {
    monthlyConsumption.value = []
    isLoadingChart.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        monthlyConsumption.value = await fetchMonthlyConsumption(token, deviceId, selectedYear.value)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingChart.value = false
    }
}

watch(selectedYear, () => {
    if (selectedDevice.value) loadMonthlyChart(selectedDevice.value.id.id)
})

// ── Selection ─────────────────────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    monthlyConsumption.value = []
    calendarMonth.value = null
    dailyData.value = []
    selectedDay.value = null
    hourlyData.value = []
    pickerOpen.value = false
    loadLatestTelemetry(device.id.id)
    loadMonthlyChart(device.id.id)
}

// ── Sélecteur mobile ─────────────────────────────────────────────────

const pickerOpen = ref(false)

// ── Load ──────────────────────────────────────────────────────────────────────

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    activeId.value = null
    try {
        const token = await getTbToken()
        if (!token) return
        allDevices.value = await fetchWmeterDevices(token)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer un compteur ─────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur
        // pour ne pas écraser les champs modifiés ailleurs entre-temps.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom du compteur modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M4,6V18H11V6H4M20,18V6H18.76C19,6.54 18.95,7.07 18.95,7.13C18.88,7.8 18.41,8.5 18.24,8.75L15.91,11.3L19.23,11.28L19.24,12.5L14.04,12.47L14,11.47C14,11.47 17.05,8.24 17.2,7.95C17.34,7.67 17.91,6 16.5,6C15.27,6.05 15.41,7.3 15.41,7.3L13.87,7.31C13.87,7.31 13.88,6.65 14.25,6H13V18H15.58L15.57,17.14L16.54,17.13C16.54,17.13 17.45,16.97 17.46,16.08C17.5,15.08 16.65,15.08 16.5,15.08C16.37,15.08 15.43,15.13 15.43,15.95H13.91C13.91,15.95 13.95,13.89 16.5,13.89C19.1,13.89 18.96,15.91 18.96,15.91C18.96,15.91 19,17.16 17.85,17.63L18.37,18H20M8.92,16H7.42V10.2L5.62,10.76V9.53L8.76,8.41H8.92V16Z" size="26" />
                    </v-avatar>
                    <div v-if="!smAndDown">
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Compteurs d'eau</div>
                    </div>
                    <!-- Sur mobile l'arbre est masqué : la sélection passe par cette puce. -->
                    <v-chip
                        v-else
                        class="selecteur-chip"
                        color="primary"
                        variant="tonal"
                        prepend-icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z"
                        append-icon="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                        @click="pickerOpen = true"
                    >
                        {{ selectedDevice ? (selectedDevice.label || selectedDevice.name) : 'Choisir un compteur' }}
                    </v-chip>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        color="primary"
                        variant="tonal"
                        rounded="pill"
                        :icon="smAndDown ? 'M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z' : undefined"
                        :prepend-icon="smAndDown ? undefined : 'M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z'"
                        :size="smAndDown ? 'small' : undefined"
                        :loading="isLoading"
                        @click="loadDevices"
                    >
                        <template v-if="!smAndDown">Rafraîchir</template>
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden; min-height: 0;">

            <!-- Left: device list tree (desktop) -->
            <div v-if="!smAndDown" class="tree-panel d-flex flex-column">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field
                        v-model="deviceSearch"
                        label="Rechercher un compteur"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        hide-details
                        clearable
                        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                    />
                </div>

                <v-divider />

                <!-- Tree -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list density="compact" nav>
                        <v-list-item
                            v-for="device in filteredDevices"
                            :key="device.id.id"
                            density="compact"
                            rounded="lg"
                            :active="activeId === device.id.id"
                            color="primary"
                            @click="onSelectDevice(device)"
                        >
                            <template #prepend>
                                <v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="16" color="primary" class="mr-2" />
                            </template>
                            <v-list-item-title class="text-caption font-weight-medium">
                                {{ device.label || device.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="device.label" class="text-caption">
                                {{ device.name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>

                    <div
                        v-if="!isLoading && filteredDevices.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption"
                    >
                        Aucun compteur trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail -->
            <div class="detail-panel">
                <!-- Placeholder -->
                <template v-if="!selectedDevice">
                    <div
                        class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                        style="height: 100%; gap: 12px;"
                    >
                        <v-icon icon="M10,9A1,1 0 0,1 11,8A1,1 0 0,1 12,9V13.47L13.21,13.6L18.15,15.79C18.68,16.03 19,16.56 19,17.14V21.5C18.97,22.32 18.32,22.97 17.5,23H11C10.62,23 10.26,22.85 10,22.57L5.1,18.37L5.84,17.6C6.03,17.39 6.3,17.28 6.58,17.28H6.8L10,19V9M11,5A4,4 0 0,1 15,9C15,10.5 14.2,11.77 13,12.46V11.24C13.61,10.69 14,9.89 14,9A3,3 0 0,0 11,6A3,3 0 0,0 8,9C8,9.89 8.39,10.69 9,11.24V12.46C7.8,11.77 7,10.5 7,9A4,4 0 0,1 11,5Z" size="48" />
                        <span class="text-body-2 text-center">
                            {{ smAndDown ? 'Touchez « Choisir un compteur » ci-dessus' : 'Sélectionnez un compteur dans la liste' }}
                        </span>
                        <span v-if="!isLoading && allDevices.length === 0 && !loginError" class="text-body-2 text-center">
                            Contactez-nous pour avoir ces capteurs sur votre ferme !
                            <a href="https://www.agrimetrie.com/compteur-deau" target="_blank" rel="noopener noreferrer">
                                Cliquez ici pour plus d'informations.
                            </a>
                        </span>
                    </div>
                </template>

                <!-- Detail card -->
                <template v-else>
                    <div class="detail-scroll">
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="20" />
                            </v-avatar>
                            <div style="flex: 1 1 140px; min-width: 0;">
                                <div class="text-subtitle-1 font-weight-bold text-truncate">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis text-truncate">
                                    {{ selectedDevice.name }}
                                </div>
                            </div>
                            <!-- Diagnostics chips -->
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="latestBatteryVoltage !== null"
                                    size="x-small"
                                    :color="latestBatteryVoltage >= 3.5 ? 'success' : latestBatteryVoltage >= 3.2 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="latestBatteryVoltage >= 3.5 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 7H9V10H15V7M15 11.5H9V14.5H15V11.5Z' : latestBatteryVoltage >= 3.2 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 11.5H9V14.5H15V11.5Z' : 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16'"
                                >
                                    {{ latestBatteryVoltage.toFixed(2) }} V
                                </v-chip>
                                <v-chip
                                    v-if="latestRssi !== null"
                                    size="x-small"
                                    :color="latestRssi >= -70 ? 'success' : latestRssi >= -85 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="latestRssi >= -70 ? 'M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : latestRssi >= -85 ? 'M19.5,5.5V18.5H17.5V5.5H19.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : 'M19.5,5.5V18.5H17.5V5.5H19.5M12.5,10.5V18.5H10.5V10.5H12.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z'"
                                >
                                    {{ latestRssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="latestSnr !== null"
                                    size="x-small"
                                    color="secondary"
                                    variant="tonal"
                                    prepend-icon="M16.5,21C13.5,21 12.31,16.76 11.05,12.28C10.14,9.04 9,5 7.5,5C4.11,5 4,11.93 4,12H2C2,11.63 2.06,3 7.5,3C10.5,3 11.71,7.25 12.97,11.74C13.83,14.8 15,19 16.5,19C19.94,19 20.03,12.07 20.03,12H22.03C22.03,12.37 21.97,21 16.5,21Z"
                                >
                                    {{ latestSnr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="primary" />

                            <v-menu v-if="peutRenommer">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" variant="text" size="small" />
                                </template>
                                <v-list density="compact">
                                    <v-list-item
                                        prepend-icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                        title="Renommer le compteur"
                                        @click="openRenameDialog"
                                    />
                                </v-list>
                            </v-menu>
                        </v-card-title>

                        <v-divider />

                        <v-card-text class="pa-3">
                            <!-- Type chip -->
                            <div class="mb-3">
                                <v-chip size="small" color="primary" variant="tonal" prepend-icon="M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z">
                                    {{ selectedDevice.type || '—' }}
                                </v-chip>
                            </div>

                            <!-- Latest telemetry -->
                            <div v-if="isLoadingTelemetry" class="d-flex align-center" style="gap: 8px;">
                                <v-progress-circular size="16" width="2" indeterminate color="primary" />
                                <span class="text-caption text-medium-emphasis">Chargement...</span>
                            </div>
                            <div v-else class="d-flex flex-wrap align-start" style="gap: 16px;">
                                <!-- Cumulative -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Cumulatif</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span>{{ latestCumul !== null ? latestCumul.toFixed(2) : '\u2014' }}</span>
                                        <span v-if="latestCumul !== null" class="text-body-2 text-medium-emphasis pb-1">m³</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Current flow -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Débit actuel</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span>{{ latestCurrentFlow !== null ? latestCurrentFlow.toFixed(3) : '\u2014' }}</span>
                                        <span v-if="latestCurrentFlow !== null" class="text-body-2 text-medium-emphasis pb-1">m³/h</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Last seen -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Dernière donnée</div>
                                    <div class="text-body-2 font-weight-bold">{{ timeSinceLastTelemetry ?? '\u2014' }}</div>
                                    <div v-if="latestTs" class="text-caption text-medium-emphasis">
                                        {{ new Date(latestTs).toLocaleString('fr-CA') }}
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Monthly consumption bar chart -->
                    <v-card variant="outlined" rounded="lg" class="mt-4" :class="{ 'mb-0': calendarMonth !== null }">
                        <v-card-title class="text-subtitle-2 py-2 px-4 d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" size="18" />
                                Consommation mensuelle
                            </div>
                            <v-btn-toggle
                                v-model="selectedYear"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                mandatory
                                rounded="lg"
                            >
                                <v-btn
                                    v-for="y in availableYears"
                                    :key="y"
                                    :value="y"
                                    size="small"
                                    :style="smAndDown ? 'min-width: 44px;' : 'min-width: 52px;'"
                                >
                                    {{ y }}
                                </v-btn>
                            </v-btn-toggle>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingChart"
                                class="d-flex align-center justify-center"
                                style="height: 200px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div v-else-if="monthlyConsumption.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption"
                                style="height: 200px;">
                                Aucune donnée disponible
                            </div>
                            <VChart v-else
                                :option="monthlyChartOption"
                                autoresize
                                style="height: 240px; width: 100%; cursor: pointer;"
                                @click="onMonthClick"
                            />
                        </v-card-text>
                    </v-card>
                    <!-- Daily calendar (inline, shown after clicking a month) -->
                    <v-card ref="calendarCardRef" v-if="calendarMonth !== null" variant="outlined" rounded="lg" class="mt-4">
                        <v-card-title class="d-flex align-center justify-space-between py-2 px-4" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="M9,10V12H7V10H9M13,10V12H11V10H13M17,10V12H15V10H17M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5A2,2 0 0,1 5,3H6V1H8V3H16V1H18V3H19M19,19V8H5V19H19M9,14V16H7V14H9M13,14V16H11V14H13M17,14V16H15V14H17Z" size="18" />
                                {{ fullMonthNames[calendarMonth] }} {{ calendarYear }}
                            </div>
                            <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="x-small" @click="calendarMonth = null" />
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-3">
                            <div v-if="isLoadingCalendar" class="d-flex align-center justify-center" style="height: 160px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <template v-else>
                                <!-- Day-of-week headers -->
                                <div class="calendar-grid mb-1">
                                    <div v-for="h in ['L', 'M', 'M', 'J', 'V', 'S', 'D']" :key="h"
                                        class="text-caption text-medium-emphasis text-center font-weight-bold py-1">
                                        {{ h }}
                                    </div>
                                </div>
                                <!-- Day cells -->
                                <div class="calendar-grid">
                                    <div v-for="n in calendarFirstDow" :key="'e' + n" />
                                    <div
                                        v-for="d in dailyData"
                                        :key="d.day"
                                        class="calendar-day rounded-lg pa-1 text-center"
                                        :class="[
                                            d.hasUsage ? 'calendar-day--usage' : d.value === null ? 'calendar-day--empty' : 'calendar-day--no-usage',
                                            d.hasUsage ? 'calendar-day--clickable' : '',
                                            selectedDay === d.day ? 'calendar-day--selected' : '',
                                        ]"
                                        @click="d.hasUsage ? openDayChart(d.day) : undefined"
                                    >
                                        <div class="text-caption font-weight-bold" style="line-height: 1.2;">{{ d.day }}</div>
                                        <div class="text-caption" style="font-size: 9px; line-height: 1.2; white-space: nowrap;">
                                            {{ d.value !== null ? d.value.toFixed(2) : '—' }}
                                        </div>
                                        <div v-if="d.value !== null" class="text-caption text-medium-emphasis" style="font-size: 8px; line-height: 1;">m³</div>
                                    </div>
                                </div>
                                <!-- Legend -->
                                <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--usage rounded" />
                                        <span class="text-caption text-medium-emphasis">Consommation</span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--no-usage rounded" />
                                        <span class="text-caption text-medium-emphasis">Pas de variation</span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--empty rounded" />
                                        <span class="text-caption text-medium-emphasis">Aucune donnée</span>
                                    </div>
                                </div>
                            </template>
                        </v-card-text>
                    </v-card>

                    <!-- Hourly chart (inline, shown after clicking a day) -->
                    <v-card ref="hourlyCardRef" v-if="selectedDay !== null" variant="outlined" rounded="lg" class="mt-4">
                        <v-card-title class="d-flex align-center justify-space-between py-2 px-4" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="M3,14L3.5,14.07L8.07,9.5C7.89,8.85 8.06,8.11 8.59,7.59C9.37,6.8 10.63,6.8 11.41,7.59C11.94,8.11 12.11,8.85 11.93,9.5L14.5,12.07L15,12C15.18,12 15.35,12 15.5,12.07L19.07,8.5C19,8.35 19,8.18 19,8A2,2 0 0,1 21,6A2,2 0 0,1 23,8A2,2 0 0,1 21,10C20.82,10 20.65,10 20.5,9.93L16.93,13.5C17,13.65 17,13.82 17,14A2,2 0 0,1 15,16A2,2 0 0,1 13,14L13.07,13.5L10.5,10.93C10.18,11 9.82,11 9.5,10.93L4.93,15.5L5,16A2,2 0 0,1 3,18A2,2 0 0,1 1,16A2,2 0 0,1 3,14Z" size="18" />
                                {{ selectedDay }} {{ calendarMonth !== null ? fullMonthNames[calendarMonth] : '' }} {{ calendarYear }}
                            </div>
                            <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="x-small" @click="selectedDay = null" />
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingHourly" class="d-flex align-center justify-center" style="height: 160px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <VChart v-else
                                :option="hourlyChartOption"
                                autoresize
                                style="height: 200px; width: 100%;"
                            />
                        </v-card-text>
                    </v-card>
                    </div>
                </template>
            </div>
        </div>
    </v-card>

    <!-- Sélecteur de compteur (mobile) -->
    <v-dialog v-model="pickerOpen" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card>
            <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px;">
                <v-icon icon="M4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M4,6V18H11V6H4M20,18V6H18.76C19,6.54 18.95,7.07 18.95,7.13C18.88,7.8 18.41,8.5 18.24,8.75L15.91,11.3L19.23,11.28L19.24,12.5L14.04,12.47L14,11.47C14,11.47 17.05,8.24 17.2,7.95C17.34,7.67 17.91,6 16.5,6C15.27,6.05 15.41,7.3 15.41,7.3L13.87,7.31C13.87,7.31 13.88,6.65 14.25,6H13V18H15.58L15.57,17.14L16.54,17.13C16.54,17.13 17.45,16.97 17.46,16.08C17.5,15.08 16.65,15.08 16.5,15.08C16.37,15.08 15.43,15.13 15.43,15.95H13.91C13.91,15.95 13.95,13.89 16.5,13.89C19.1,13.89 18.96,15.91 18.96,15.91C18.96,15.91 19,17.16 17.85,17.63L18.37,18H20M8.92,16H7.42V10.2L5.62,10.76V9.53L8.76,8.41H8.92V16Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Compteurs d'eau</span>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="pickerOpen = false" />
            </v-card-title>
            <div class="px-4 pb-3">
                <v-text-field
                    v-model="deviceSearch"
                    label="Rechercher un compteur"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    clearable
                    prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                />
            </div>
            <v-divider />
            <v-card-text class="pa-0">
                <v-list density="compact" nav>
                    <v-list-item
                        v-for="device in filteredDevices"
                        :key="device.id.id"
                        rounded="lg"
                        :active="activeId === device.id.id"
                        color="primary"
                        @click="onSelectDevice(device)"
                    >
                        <template #prepend>
                            <v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="18" color="primary" class="mr-2" />
                        </template>
                        <v-list-item-title class="text-body-2 font-weight-medium">
                            {{ device.label || device.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="device.label" class="text-caption">
                            {{ device.name }}
                        </v-list-item-subtitle>
                    </v-list-item>
                </v-list>
                <div
                    v-if="!isLoading && filteredDevices.length === 0"
                    class="pa-6 text-center text-medium-emphasis text-caption"
                >
                    Aucun compteur trouvé
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Renommer -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer le compteur</span>
            </v-card-title>
            <v-card-text class="px-5">
                <div class="text-caption text-medium-emphasis mb-3">
                    Identifiant technique : {{ selectedDevice?.name }}
                </div>
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    autofocus
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn color="primary" variant="flat" rounded="pill" :loading="isSavingLabel" @click="saveLabel">
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la carte occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

/* Le nom du compteur peut être long : la puce se tronque au lieu de pousser le bouton hors écran. */
.selecteur-chip {
    max-width: 55vw;
    transition: opacity 0.15s;
}
.selecteur-chip:hover {
    opacity: 0.78;
}
.selecteur-chip :deep(.v-chip__content) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tree-panel {
    flex-shrink: 0;
    width: 300px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.detail-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

.sensor-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}

/* ── Calendar ────────────────────────────── */
.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

.calendar-day {
    min-height: 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.calendar-day--usage {
    background: rgba(var(--v-theme-success), 0.18);
    color: rgb(var(--v-theme-success));
}

.calendar-day--clickable {
    cursor: pointer;
}

.calendar-day--clickable:hover {
    background: rgba(var(--v-theme-success), 0.32);
}

.calendar-day--selected {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
}

.calendar-day--no-usage {
    background: rgba(var(--v-border-color), 0.08);
}

.calendar-day--empty {
    background: transparent;
    opacity: 0.4;
}

.calendar-legend-dot {
    width: 12px;
    height: 12px;
}
</style>
